<template>
  <div class="highlights-select-modal">
    <!-- Header -->
    <div class="header highlights-select-modal__header">
      <div class="header__back-link-wrap">
        <LinkWithIcon class="header__back-link" @click="emit('cancel')" isDottedUnderline>
          <template #icon><IconEmbedded name="arrow-left_2-5" :size="16" /></template>
          <span>Back</span>
        </LinkWithIcon>
      </div>

      <h2 class="global-h2 header__title">{{ displayedAlbumTitle }}</h2>
      <div v-if="props.subTitle" class="header__subtitle">
        {{ props.subTitle }}
      </div>
    </div>
    <!-- / Header -->

    <div v-if="pickingFromContentSummaryId" class="album-switcher highlights-select-modal__album-switcher">
      <LinkWithIcon isDottedUnderline @click="clearExternalAlbum()">
        <template #icon><IconEmbedded name="arrow-left_2-5" :size="16" /></template>
        <span>Show original album</span>
      </LinkWithIcon>
    </div>

    <PostMediaSelectionPanel
      v-model:selectedAssetIds="_selectedAssetIds"
      v-model:uploadedAssetIds="uploadedAssetIds"
      :customerId="props.customerId"
      :postType="props.postType"
      :displayedAssetIds="displayedAssetIds"
      inputId="social-post-upload"
      galleryPostfix="postPhotoSelectionModal"
      :isLoadingAlbum="isLoadingExternalAlbum"
      :showThumbnailGrid="true"
      :showAlbumPickerLink="!pickingFromContentSummaryId"
      albumPickerLabel="Select from another album"
      :hasAlbum="hasAlbumForNoVideosMessage"
      @openAlbumPicker="isPickContentModalVisible = true"
    />

    <PickFromExistingContentModal
      v-if="isPickContentModalVisible"
      v-model:isVisible="isPickContentModalVisible"
      :customerId="props.customerId"
      :calendarItem="null"
      :videoOnly="videoOnlyForAlbumPicker"
      :showPhotoAndVideoCount="showAlbumPhotoAndVideoCount"
      @close="isPickContentModalVisible = false"
      @picked="handleContentSummarySelected"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, toRef } from "vue";

import LinkWithIcon from "@components/LinkWithIcon.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import PickFromExistingContentModal from "@views/Community/ContentCalendar/steps/modals/PickFromExistingContentModal.vue";
import PostMediaSelectionPanel from "./PostMediaSelectionPanel.vue";

import ShareStopSummaryRepo from "@repos/ShareStopSummaryRepo";

import { SocialShareSummaryPayload, SocialShareSummaryContentType, SocialPostPublishType } from "@contracts/shareStop";
import { ContentSummaryPreview } from "@contracts/contentCalendar";

import { filterAlbumAssetsForPostType, getDefaultPhotoSelectorTitle } from "@logic/AtlasSchedulingValidation";
import { usePostTypeMediaRules } from "@composables/usePostTypeMediaRules";

const props = withDefaults(
  defineProps<{
    customerId: string;
    title?: string;
    subTitle?: string;
    allAssetIds: Array<string>;
    selectedAssetIds: Array<string>;
    postType?: SocialPostPublishType;
  }>(),
  {
    customerId: "",
    title: "Select Photos",
    subTitle: "",
    allAssetIds: () => [],
    selectedAssetIds: () => [],
    postType: SocialPostPublishType.Post,
  }
);

const emit = defineEmits<{
  (e: "done", selectedAssetIds: Array<string>): void;
  (e: "cancel"): void;
}>();

const { videoOnlyForAlbumPicker, showAlbumPhotoAndVideoCount } = usePostTypeMediaRules(toRef(props, "postType"));

const isPickContentModalVisible = ref<boolean>(false);
const pickingFromContentSummaryId = ref<string | null>(null);
const externalAlbumPayload = ref<SocialShareSummaryPayload | null>(null);
const isLoadingExternalAlbum = ref<boolean>(false);
const uploadedAssetIds = ref<Array<string>>([]);
const selectedAssetsNotInContentAlbum = ref<Array<string>>([]);
const _selectedAssetIds = ref<Array<string>>([]);

onMounted(() => {
  _selectedAssetIds.value = [...props.selectedAssetIds];
  selectedAssetsNotInContentAlbum.value = props.selectedAssetIds.filter(assetId => !props.allAssetIds.includes(assetId));
});

const displayedAssetIds = computed(() => {
  if (pickingFromContentSummaryId.value && externalAlbumPayload.value) {
    return filterAlbumAssetsForPostType(externalAlbumPayload.value.assets, props.postType);
  }
  return filterAlbumAssetsForPostType([...props.allAssetIds, ...selectedAssetsNotInContentAlbum.value], props.postType);
});

const hasAlbumForNoVideosMessage = computed(() => {
  if (pickingFromContentSummaryId.value) {
    return Boolean(externalAlbumPayload.value);
  }
  return props.allAssetIds.length > 0;
});

const displayedAlbumTitle = computed(() => {
  if (pickingFromContentSummaryId.value && externalAlbumPayload.value) {
    return `Select from: "${externalAlbumPayload.value.title}"`;
  }
  return props.title === "Select Photos" ? getDefaultPhotoSelectorTitle(props.postType) : props.title;
});

async function done() {
  emit("done", _selectedAssetIds.value);
}

async function handleContentSummarySelected(contentSummary: ContentSummaryPreview) {
  isPickContentModalVisible.value = false;
  pickingFromContentSummaryId.value = contentSummary.id;

  isLoadingExternalAlbum.value = true;
  externalAlbumPayload.value = await ShareStopSummaryRepo.loadSocialShareSummaryPayloadFromSummaryId(
    props.customerId,
    contentSummary.id,
    SocialShareSummaryContentType.All
  );
  isLoadingExternalAlbum.value = false;
}

function clearExternalAlbum() {
  pickingFromContentSummaryId.value = null;
  externalAlbumPayload.value = null;
}

defineExpose({
  done,
  cancel: () => emit("cancel"),
});
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";

.header {
  &__back-link-wrap {
    padding-bottom: 15px;
    margin-bottom: 20px;
    border-bottom: 1px rgba(0, 0, 0, 0.15) solid;
  }

  &__title {
    margin-bottom: 10px;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.album-switcher {
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.highlights-select-modal {
  color: #5b5b5b;
  font: 14px/19px sans-serif;

  &__header {
    margin-bottom: 15px;
  }

  &__album-switcher {
    margin-bottom: 15px;
  }
}
</style>
